import { Breadcrumbs, Stack, Typography } from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'

import Content from 'components/common/Content'
import { EmptyStateMessage } from 'components/common/EmptyStateMessage'
import PageSection from 'components/common/PageSection'
import Icon from 'components/Icon'
import Header from 'components/navigation/Header'
import DashboardServiceList from 'components/reliability-management/company-dashboard/DashboardServiceList'
import DashboardSummary from 'components/reliability-management/company-dashboard/DashboardSummary'
import { ServiceErrorRetryAlert } from 'components/services/common/ServiceErrorRetryAlert'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { useRetryAsync } from 'hooks/use-retry-async'
import theme from 'theme'
import { CompanyReliabilityReportResponse } from 'types/api/policies/reliability-report'
import { messageFromResponseError } from 'utils/strings'

const CompanyDashboardPage: React.FC = () => {
  const {
    data: report,
    isFetching: isFetchingReport,
    isError: hasErrorReport,
    error,
    refetch: fetchReport
  } = useGremlinQuery<CompanyReliabilityReportResponse>(
    'reports/companies/reliability-management',
    {},
    {
      enabled: true,
      placeholderData: {} as CompanyReliabilityReportResponse
    }
  )

  const { companyDisplayName } = useAuth()

  const { isRetrying: isRetryingReport, doRetry: doRetryReport } =
    useRetryAsync(fetchReport)

  return (
    <>
      <Helmet title="Dashboard" />
      <Header>
        <Breadcrumbs aria-label="breadcrumb">
          <Stack alignItems="center" direction="row">
            <Icon css={{ marginRight: theme.space[2] }} icon="chartLineUp" />{' '}
            <Typography color="text.primary">Dashboard</Typography>
          </Stack>
        </Breadcrumbs>
      </Header>
      <Content
        css={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gridAutoRows: 'min-content',
          gap: theme.space[6]
        }}
      >
        <Stack>
          <Typography data-test-company-dashboard-header variant="h5">
            Dashboard
          </Typography>
          {companyDisplayName && (
            <Typography data-test-company-dashboard-description>
              This dashboard shows the progress in all the teams in your
              Company, {companyDisplayName}.
            </Typography>
          )}
        </Stack>
        <PageSection
          data-test-dashboard // note: this is the same as the old dashboard, so it satisfies post-auth tests
          errorMessage={messageFromResponseError(error, 'There was a problem ')}
          hasError={hasErrorReport}
          isLoading={isFetchingReport}
          renderError={() => (
            <ServiceErrorRetryAlert
              alertText="Company Dashboard data could not be loaded"
              isRetrying={isRetryingReport}
              onClick={doRetryReport}
            />
          )}
        >
          {() => {
            return report?.services?.length < 1 ? (
              <EmptyStateMessage
                data-test-company-dashboard-empty
                icon="chartNetwork"
                title="No Services"
              >
                <Link to="/services/new">Define a service</Link> to get started
                with Reliability Management
              </EmptyStateMessage>
            ) : (
              <Stack direction="column" gap={5}>
                {report?.summary?.biggestChangers?.length > 0 && (
                  <DashboardSummary report={report} />
                )}
                <DashboardServiceList report={report} />
              </Stack>
            )
          }}
        </PageSection>
      </Content>
    </>
  )
}

export default CompanyDashboardPage
